import { normalize } from './text'
import { getEntry, entryPath } from './guideIndex'
import { prettyItemPath, prettyMapPath, prettyMonsterPath, prettySpotPath } from './prettyRoutes'
import type { Spawn, WorldData, WorldMap } from '../types/world'

export const hasCoords = (s: Spawn) => [s.xMin, s.xMax, s.yMin, s.yMax].every((v) => v !== undefined)
export const isPoint = (s: Spawn) => hasCoords(s) && s.xMin === s.xMax && s.yMin === s.yMax

const range = (a: number, b: number) => (a === b ? `${a}` : `${Math.min(a, b)}–${Math.max(a, b)}`)

/** Coordenada puntual ("X: 174 / Y: 106") o área ("X: 125–139 / Y: 190–202"). Nunca convierte un área en un punto. */
export function coordLabel(s: Spawn): string | undefined {
  if (!hasCoords(s)) return undefined
  return `X: ${range(s.xMin!, s.xMax!)} / Y: ${range(s.yMin!, s.yMax!)}`
}

export function locationCopyText(mapName: string, monsterName: string, s: Spawn): string {
  return `${mapName} — ${monsterName} — ${coordLabel(s) ?? 'coordenadas no documentadas'}`
}

export function prettyAccessName(name: string): string {
  return name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Za-z])(\d)/g, '$1 $2')
}

/** Niveles de acceso documentados (cada subzona por separado). Vacío = no documentado. */
export function documentedAccessLevels(m: WorldMap): number[] {
  const levels = m.moveEntries.map((e) => e.minLevel).filter((v): v is number => v !== undefined)
  if (levels.length) return levels
  return m.minimumMoveLevel !== undefined ? [m.minimumMoveLevel] : []
}

export function formatSeconds(s: number): string {
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  const r = s % 60
  return r ? `${m} min ${r} s` : `${m} min`
}

export const fmt = (n?: number) => (n === undefined ? undefined : n.toLocaleString('es-ES'))

export const nameKey = (n: string) => normalize(n).replace(/[’']/g, '').replace(/\s+/g, ' ').trim()

// ── Rutas ──
// Rutas legibles por categoría y nombre (utils/prettyRoutes). Antes de cargar los archivos se usa la ruta por ID,
// que sigue funcionando y redirige a la legible.
export const mapPath = (id: number) => prettyMapPath(id) ?? `/guia/mapas/mapa/${id}`
export const monsterPath = (id: number) => prettyMonsterPath(id) ?? `/guia/mapas/monstruo/${id}`
export const spotPath = (id: string) => prettySpotPath(id) ?? `/guia/mapas/spot/${encodeURIComponent(id)}`
export const itemKey = (id: string) => id.replace(',', '-').replace('@', '_')
export const itemIdFromKey = (key: string) => key.replace('-', ',').replace('_', '@')
export const itemPath = (id: string) => prettyItemPath(id) ?? `/guia/items/${itemKey(id)}`

export const mapName = (w: WorldData, id?: number) => (id === undefined ? 'Sin mapa' : w.maps.get(id)?.name ?? `Mapa ${id} (no encontrado)`)
export const monsterName = (w: WorldData, id?: number) => (id === undefined ? 'Sin monstruo' : w.monsters.get(id)?.name ?? `Monstruo ${id} (no encontrado)`)
/** Clases compatibles con un ítem, solo las activas en 04_classes (active = True). Las demás no se publican como disponibles. */
export const activeItemClasses = (w: WorldData, itemId: string): string[] =>
  Array.from(new Set(w.itemClasses.get(itemId) ?? [])).filter((c) => w.activeClassIds.size === 0 || w.activeClassIds.has(c))
export const itemName = (w: WorldData, id?: string) => (id === undefined ? 'Sin ítem' : w.variants.get(id)?.name ?? w.items.get(id)?.name ?? `${id} (no encontrado)`)

export const kindLabel: Record<string, string> = { spot: 'Spot', invasion: 'Invasión', event: 'Evento', ordinary: 'Permanente' }

export type ResolvedEvent = { label: string; path?: string; linked: boolean; isInvasion: boolean }

const titleCase = (s: string) => s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/** Nombre legible de un event_id. Solo enlaza a una ficha si la equivalencia se comprobó. */
export function resolveEvent(w: WorldData, eventId: string): ResolvedEvent {
  const link = w.eventLinks.get(eventId)
  const isInvasion = eventId.startsWith('invasion:')
  if (link?.status === 'verified' && link.guideEntryId) {
    const entry = getEntry(link.guideEntryId)
    if (entry) return { label: entry.title, path: entryPath(entry), linked: true, isInvasion }
  }
  if (isInvasion) {
    const slug = link?.slugs.length === 1 ? link.slugs[0] : undefined
    return { label: slug ? `Invasión ${titleCase(slug)}` : `Invasión (${eventId})`, linked: false, isInvasion }
  }
  return { label: titleCase(eventId.replace(/^event:/, '')), linked: false, isInvasion }
}
